import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { DialogContainer } from "@components/common";
import GoCaptcha from "go-captcha-react";
import {
  getCaptchaClickShape,
  checkCaptchaClickShape,
  type SignUpRequest,
} from "@src/utils/api/account";
import { useAppDispatch } from "@src/store";
import { sendErrorNotification } from "@src/utils";

interface ClickShapeCaptProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onConfirm(captcha: SignUpRequest["captcha"]): any;
}

export default function ShapeCaptDialog({
  open,
  setOpen,
  onConfirm,
}: ClickShapeCaptProps) {
  const { t } = useTranslation();
  const [captchaData, setCaptchaData] = useState({
    imageBase64: "",
    thumbBase64: "",
  });
  const dispatch = useAppDispatch();
  const [captchaKey, setCaptchaKey] = useState("");

  const requestCaptchaData = useCallback(async () => {
    try {
      const data = await getCaptchaClickShape();
      setCaptchaData({
        imageBase64: data.imageBase64,
        thumbBase64: data.thumbBase64,
      });
      setCaptchaKey(data.captchaKey);
      // sendSuccessNotification(t("captcha.success"), dispatch);
    } catch (error) {
      console.error("Failed to get captcha data:", error);
      sendErrorNotification(error, dispatch);
    }
  }, [setCaptchaKey, dispatch]);

  useEffect(() => {
    if (open) requestCaptchaData();
    else {
      setCaptchaKey("");
      setCaptchaData({
        imageBase64: "",
        thumbBase64: "",
      });
    }
  }, [open, requestCaptchaData]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleRefresh = useCallback(() => {
    requestCaptchaData();
  }, [requestCaptchaData]);

  const handleConfirm = useCallback(
    async (
      dots: { x: number; y: number }[],
      clear: (fn: () => void) => void
    ) => {
      try {
        const response = await checkCaptchaClickShape({
          dots: dots.flatMap((dot) => [dot.x, dot.y]),
          key: captchaKey,
        });

        if (response.found) {
          setOpen(false);
          onConfirm({ captchaKey, captchaCode: response.captchaCode });
        } else {
          throw Error(t("captcha.invalid"));
          // reset key and code
        }

        setTimeout(() => {
          clear(() => {});
        }, 1000);
      } catch (error) {
        console.error("Failed to check captcha:", error);
        sendErrorNotification(error, dispatch);
        handleClose();
        // reset key and code
      }
    },
    [captchaKey, t, setOpen, dispatch, handleClose, onConfirm]
  );

  return (
    <>
      {/* <Button
        onClick={() => {
          setOpen(true);
          setCaptchaKey("");
          setCaptchaCode("");
        }}
        className={`flex w-full justify-center rounded-md border border-transparent py-2 px-4 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
          captchaKey && captchaCode
            ? "bg-green-600 hover:bg-green-700 focus:ring-green-500"
            : "bg-primary hover:bg-primary focus:ring-primary"
        } disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-500`}
      >
        {captchaKey && captchaCode ? t("captcha.success") : t("captcha.button")}
      </Button> */}
      <DialogContainer open={open} setOpen={setOpen}>
        <GoCaptcha.Click
          config={{
            width: 300,
            height: 240,
            showTheme: false,
            verticalPadding: 5,
            horizontalPadding: 5,
          }}
          data={{
            image: captchaData.imageBase64,
            thumb: captchaData.thumbBase64,
          }}
          events={{
            close: handleClose,
            refresh: handleRefresh,
            confirm: handleConfirm,
          }}
        />
      </DialogContainer>
    </>
  );
}
